import React, { useState, useEffect } from 'react';
import { collection, getDocs, onSnapshot, doc,getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaShareAlt } from 'react-icons/fa';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Helmet } from 'react-helmet';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { leagueToolsTranslations } from '../i18n/translations';



const isMatchNotPlayed = (sets) => {
  return sets.every((set) => set.teamAScore === 0 && set.teamBScore === 0);
};



const LeagueClassement = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, leagueToolsTranslations);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const shareOnSocialMedia = (leagueId) => {
    const shareUrl = `${window.location.origin}/league/${leagueId}`;
    const text = `Découvrez le classement de la Ligue ${leagueId} : ${shareUrl}`;
    const shareData = { title: 'Classement Ligue', text, url: shareUrl };
  
    if (navigator.share) {
      navigator.share(shareData).catch((err) => console.error('Erreur lors du partage', err));
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert(t('copied'));
    }
  };

  const handleDownloadClassementPDF = async (league) => {
    const doc = new jsPDF();
  
    // Ajouter un titre
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(`Classement - Ligue ${league.leagueId}`, 10, 20);
  
    // Ajouter un soulignement
    const textWidth = doc.getTextWidth(`Classement - Ligue ${league.leagueId}`);
    doc.setDrawColor(128, 0, 32); // Rouge sombre
    doc.setLineWidth(1);
    doc.line(10, 22, 10 + textWidth, 22);
  
    // Ajouter le logo
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
      });
    };
  
    try {
      const logo = await loadImage('/logo2.jpg'); // Chemin du logo
      doc.addImage(logo, 'JPEG', 160, 10, 30, 30); // Position et taille ajustées
    } catch (error) {
      console.error('Erreur lors du chargement du logo :', error);
    }
  
    // Préparer les données pour le tableau
    const tableData = league.teamsRanking.map((team, rank) => [
      rank + 1, // Rang
      team.teamName, // Nom de l'équipe
      team.points, // Points
      team.wins, // Victoires
      team.draw,
      team.losses, // Défaites
      
      team.gamesWon, // Jeux Gagnés
      team.gamesLost, // Jeux Perdus
      team.gamesDiff, // Différence
    ]);
  
    console.log("Données du tableau pour export PDF :", tableData);

    // Ajouter le tableau avec le head en rouge
    doc.autoTable({
      head: [
        ['Rang', 'Nom', 'Points', 'Victoires','Matchs Nuls', 'Défaites', 'Jeux Gagnés', 'Jeux Perdus', 'Diff.'],
      ],
      body: tableData,
      startY: 40, // Position sous le titre
      headStyles: { fillColor: [128, 0, 32], textColor: [255, 255, 255] }, // Rouge pour le head
    });
  
    // Sauvegarder le PDF
    doc.save(`classement_ligue_${league.leagueId}.pdf`);
  };
  

  const shareClassementLink = (leagueId) => {
    const shareUrl = `${window.location.origin}/league/${leagueId}/classement`;
  
    if (navigator.share) {
      navigator
        .share({
          title: `Classement Ligue ${leagueId}`,
          text: `Découvrez le classement de la Ligue ${leagueId} !`,
          url: shareUrl,
        })
        .catch((err) => console.error('Erreur lors du partage :', err));
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert(t('copied'));
    }
  };
  
  
  const shareClassementImage = async (leagueId) => {
    const element = document.getElementById(`league-${leagueId}`); // Identifiez le tableau par ID
    if (!element) {
      alert(t('shareMissing'));
      return;
    }
  
    try {
      // Convertir l'élément HTML en image (PNG)
      const dataUrl = await toPng(element);
  
      // Vérifiez si le partage est supporté par le navigateur
      if (navigator.share) {
        const blob = await fetch(dataUrl).then((res) => res.blob());
        const file = new File([blob], `classement_ligue_${leagueId}.png`, {
          type: 'image/png',
        });
  
        await navigator.share({
          files: [file],
          title: `Classement Ligue ${leagueId}`,
          text: `Voici le classement de la ligue ${leagueId}`,
        });
      } else {
        // Téléchargez l'image si le partage direct n'est pas supporté
        const link = document.createElement('a');
        link.download = `classement_ligue_${leagueId}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (error) {
      console.error('Erreur lors de la génération ou du partage de l’image :', error);
      alert(t('shareError'));
    }
  };
  

  useEffect(() => {
    const fetchLeaguesAndScores = async () => {
      setLoading(true);
      setError(null);
  
      try {
        console.log("=== Début de la récupération des ligues ===");
  
        const leagueIDs = Array.from({ length: 2 }, (_, i) => (i + 1).toString()); // Boucle de 1 à 10 ligues
        const tempLeagues = []; // Stock temporaire pour éviter un affichage incorrect
  
        for (const leagueId of leagueIDs) {
          console.log(`📂 Tentative de récupération de la ligue ID : ${leagueId}`);
  
          // Récupérer le nom de la ligue depuis 'league_registrations'
          const leagueDocRef = doc(db, 'league_registrations', leagueId);
          const leagueDoc = await getDoc(leagueDocRef);
  
          let leagueName = `Ligue ${leagueId}`; // Par défaut si le nom n'existe pas
          if (leagueDoc.exists()) {
            const data = leagueDoc.data();
            leagueName = data.name || leagueName;
          }
  
  
          const response = await fetch(`https://us-central1-mendyx-adafe.cloudfunctions.net/leagueClassementFunctions-leagueClassementFunctions?leagueId=${leagueId}`);
          const matchesSnapshot = await response.json();
  
          if (!matchesSnapshot || !matchesSnapshot.matches || !Array.isArray(matchesSnapshot.matches)) {
            console.warn(`⚠️ Aucun match trouvé pour la ligue ID : ${leagueId}`);
            continue; // Passer à la ligue suivante si aucun match n'est trouvé
          }
  
          const matches = matchesSnapshot.matches;
  
          const teamsStats = {};
          const directConfrontations = {}; // Stocke les résultats des confrontations directes
  
          // Calcul des statistiques des équipes
          matches
            .filter(({ sets = [] }) => !isMatchNotPlayed(sets)) // Exclure les matchs non joués
            .forEach(({ teamA, teamB, sets = [] }) => {
              // Initialiser les stats pour chaque équipe
              if (!teamsStats[teamA]) {
                teamsStats[teamA] = { points: 0, wins: 0, draws: 0, losses: 0, gamesWon: 0, gamesLost: 0, setsWon: 0, setsLost: 0, advantage: 0 };
              }
              if (!teamsStats[teamB]) {
                teamsStats[teamB] = { points: 0, wins: 0, draws: 0, losses: 0, gamesWon: 0, gamesLost: 0, setsWon: 0, setsLost: 0, advantage: 0 };
              }
  
              let teamAWins = 0;
              let teamBWins = 0;
              let lastSetIncomplete = false;
              let lastSetAdvantage = null;
  
              // Calculer les résultats des sets
              sets.forEach((set, index) => {
                const teamAScore = Number(set.teamAScore) || 0;
                const teamBScore = Number(set.teamBScore) || 0;
  
                // Mise à jour des statistiques des jeux
                teamsStats[teamA].gamesWon += teamAScore;
                teamsStats[teamA].gamesLost += teamBScore;
                teamsStats[teamB].gamesWon += teamBScore;
                teamsStats[teamB].gamesLost += teamAScore;
  
                // Mise à jour des sets gagnés/perdus
                if (teamAScore > teamBScore) {
                  teamAWins += 1;
                  teamsStats[teamA].setsWon += 1;
                  teamsStats[teamB].setsLost += 1;
                } else if (teamBScore > teamAScore) {
                  teamBWins += 1;
                  teamsStats[teamB].setsWon += 1;
                  teamsStats[teamA].setsLost += 1;
                }
  
                // Vérification du dernier set inachevé
                if (index === sets.length - 1) {
                  if (teamAScore === 0 && teamBScore === 0) {
                    console.log(`Le 3ème set entre ${teamA} et ${teamB} n'a pas été joué.`);
                    return;
                  }
  
                  if (teamAScore !== 6 && teamBScore !== 6) {
                    lastSetIncomplete = true;
                    lastSetAdvantage = teamAScore > teamBScore ? teamA : teamB;
                  }
                }
              });
  
              // Gestion des résultats en fonction des règles
              if (lastSetIncomplete) {
                teamsStats[teamA].points += 2;
                teamsStats[teamB].points += 2;
                teamsStats[teamA].draws += 1;
                teamsStats[teamB].draws += 1;
  
                if (lastSetAdvantage === teamA) {
                  teamsStats[teamA].advantage += 1;
                } else if (lastSetAdvantage === teamB) {
                  teamsStats[teamB].advantage += 1;
                }
              } else if (teamAWins === 2) {
                teamsStats[teamA].points += 3;
                teamsStats[teamB].points += 1;
                teamsStats[teamA].wins += 1;
                teamsStats[teamB].losses += 1;
              } else if (teamBWins === 2) {
                teamsStats[teamB].points += 3;
                teamsStats[teamA].points += 1;
                teamsStats[teamB].wins += 1;
                teamsStats[teamA].losses += 1;
              } else if (teamAWins === 1 && teamBWins === 1) {
                teamsStats[teamA].points += 2;
                teamsStats[teamB].points += 2;
                teamsStats[teamA].draws += 1;
                teamsStats[teamB].draws += 1;
              }
            });
  
          const teamsRanking = Object.keys(teamsStats)
            .map((teamName) => {
              const stats = {
                teamName,
                ...teamsStats[teamName],
                draws: teamsStats[teamName].draws || 0,
                losses: teamsStats[teamName].losses || 0,
                setsDiff: teamsStats[teamName].setsWon - teamsStats[teamName].setsLost,
                gamesDiff: teamsStats[teamName].gamesWon - teamsStats[teamName].gamesLost,
              };
              return stats;
            })
            .sort((a, b) => {
              if (b.points !== a.points) return b.points - a.points;
              if (b.setsDiff !== a.setsDiff) return b.setsDiff - a.setsDiff;
              if (b.gamesDiff !== a.gamesDiff) return b.gamesDiff - a.gamesDiff;
              if ((b.advantage || 0) !== (a.advantage || 0)) return (b.advantage || 0) - (a.advantage || 0);
              return 0;
            });
  
          // Ajouter les classements au fur et à mesure
          if (teamsRanking.length > 0) {
            tempLeagues.push({ leagueId, name: leagueName, teamsRanking });
          }
        }
  
        setLeagues(tempLeagues); // Mettre à jour les ligues visibles
        console.log("=== Fin de la récupération des ligues ===");
      } catch (error) {
        console.error("❌ Erreur lors de la récupération des données :", error);
        setError("Une erreur est survenue lors de la récupération des données.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchLeaguesAndScores();
  }, []);
  

  return (
    <>
      <Helmet>
        <title>{t('rankingsTitle')} | Padel Mendyx</title>
        <meta
          name="description"
          content="Consultez les classements des ligues Padel organisées par Padel Mendyx. Découvrez les équipes et leurs performances."
        />
        <meta name="keywords" content="classement ligue padel, Padel Mendyx, ligues padel, classement équipes" />
        <meta property="og:title" content="Classements des Ligues | Padel Mendyx" />
        <meta
          property="og:description"
          content="Suivez les performances des équipes et découvrez les classements des ligues de Padel Mendyx."
        />
        <meta property="og:image" content="/league-ranking.jpg" />
        <meta property="og:url" content="https://padelmedyx.com/leagues" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://padelmedyx.com/leagues" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SportsEvent",
            "name": "Classements des Ligues Padel",
            "description": "Classements officiels des ligues Padel organisées par Padel Mendyx. Suivez les performances des équipes.",
            "url": "https://padelmedyx.com/leagues",
          })}
        </script>
      </Helmet>
    <section className="container mx-auto my-16 px-4">
      <h2 className="text-3xl font-semibold text-[#800020] mb-8 text-center">{t('rankingsTitle')}</h2>
      {error && <div className="text-center text-red-600">{error}</div>}
      {leagues.length === 0 && !error && (
        <div className="text-center text-xl">{t('loadingRankings')}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {leagues.map((league, index) => (
          <div
  key={index}
  id={`league-${league.leagueId}`} // Ajoutez un ID unique ici
  className="bg-white shadow-lg rounded-xl border border-gray-300 overflow-hidden p-4"
>

<div className="flex justify-between items-center mb-4">
  <h3 className="text-xl font-semibold text-[#800020]">{league.name}</h3>
  
  <div className="flex items-center gap-2">
    {/* Bouton pour télécharger le classement en PDF */}
    <button
      onClick={() => handleDownloadClassementPDF(league)}
      className="bg-gradient-to-r from-[#800020] to-[#ea4c89] text-white w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition"
      title={t('downloadRanking', { id: league.leagueId })}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 4v12m0 0l-6-6m6 6l6-6"
        />
        <rect x="4" y="19" width="16" height="2" fill="white" />
      </svg>
    </button>

    {/* Bouton pour partager le lien */}
    <button
      onClick={() => shareClassementLink(league.leagueId)}
      className="text-[#800020] hover:text-[#600018] transition"
      title={t('shareRanking', { id: league.leagueId })}
    >
      <FaShareAlt className="w-6 h-6" />
    </button>
  </div>
</div>


            {league.teamsRanking.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-400 text-sm">
                  <thead className="bg-[#800020] text-white">
                    <tr>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('rank')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('name')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('points')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('wins')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('draws')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('losses')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('setsWon')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('setsLost')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('setsDiff')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('gamesWon')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('gamesLost')}</th>
                      <th className="border border-gray-400 px-2 py-1 text-left">{t('gamesDiff')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {league.teamsRanking.map((team, rank) => (
                      <tr key={rank} className="hover:bg-gray-50 transition-colors">
                        <td className="border border-gray-400 px-2 py-1 text-center">{rank + 1}</td>
                        <td className="border border-gray-400 px-2 py-1">{team.teamName}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.points}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.wins}</td>
                        
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.draws}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.losses}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.setsWon}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.setsLost}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.setsDiff}</td>

                        <td className="border border-gray-400 px-2 py-1 text-center">{team.gamesWon}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.gamesLost}</td>
                        <td className="border border-gray-400 px-2 py-1 text-center">{team.gamesDiff}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-gray-500">{t('noMatch')}</p>
            )}
          </div>
        ))}
      </div>
    </section>
    </>
  );
};

export default LeagueClassement;
